<template>
  <div class="station-add-page gm-page pa-4 d-flex flex-column min-h-0">
    <template v-if="!submittedView">
      <div class="wizard-frame gm-card rounded-xl flex-grow-1 min-h-0">
        <aside class="wizard-sidebar">
          <div class="wizard-sidebar__toolbar">
            <v-btn variant="text" density="comfortable" class="wizard-sidebar__back px-1 align-self-start"
              @click="goBack">
              <v-icon start size="20">mdi-chevron-left</v-icon>
              返回
            </v-btn>
          </div>
          <WizardStepper :model-value="currentStep" variant="sidebar" :steps="steps" class="wizard-sidebar__stepper"
            @update:model-value="handleStepChange" />
        </aside>

        <div class="wizard-mobile-head d-flex d-lg-none flex-column ga-3">
          <div class="wizard-mobile-head__row d-flex align-center justify-space-between">
            <v-btn variant="text" density="comfortable" class="wizard-mobile-head__back px-1" @click="goBack">
              <v-icon start size="20">mdi-chevron-left</v-icon>
              返回
            </v-btn>
            <span class="wizard-mobile-head__steps text-caption font-weight-semibold text-medium-emphasis">
              步骤 {{ currentStep + 1 }} / {{ steps.length }}
            </span>
          </div>
          <div class="wizard-mobile-progress" aria-hidden="true">
            <div class="wizard-mobile-progress__fill" :style="{ width: mobileProgressPct + '%' }" />
          </div>
          <WizardStepper :model-value="currentStep" inline :steps="steps" @update:model-value="handleStepChange" />
        </div>

        <main class="wizard-main min-h-0">
          <div class="wizard-body" :class="{
            'wizard-body--device-step': currentStep === 1,
            'wizard-body--topo-step': currentStep === 2,
          }">
            <StationAddStepBasic v-if="currentStep === 0" />
            <StationAddStepDevice v-if="currentStep === 1" ref="deviceStepRef" :station-id="effectiveStationId" />
            <StationAddStepTopology v-if="currentStep === 2" ref="topologyStepRef" :saving="topologySaving"
              @update:draft="stationTopologyDraft = $event" @save="handleTopologySave" />
          </div>

          <footer class="wizard-footer">
            <div class="wizard-footer__row">
              <v-btn :variant="prevBtnIsPrimary ? 'flat' : 'text'" :color="prevBtnIsPrimary ? 'primary' : undefined"
                :size="prevBtnIsPrimary ? 'large' : undefined" rounded="lg" class="text-none"
                :class="{ 'wizard-footer__primary': prevBtnIsPrimary }" :disabled="currentStep <= 0" @click="prevStep">
                上一步
              </v-btn>
              <v-btn v-if="!isLastStep" color="primary" variant="flat" size="large" rounded="lg"
                class="wizard-footer__primary text-none" :loading="advancing" @click="nextStep">
                下一步
              </v-btn>
              <v-btn v-else-if="showSubmitBtn" color="primary" variant="flat" size="large" rounded="lg"
                class="wizard-footer__primary text-none" :loading="submitting" @click="handleSubmit">
                提交
              </v-btn>
              <v-btn v-if="canResubmitApproval" color="success" variant="flat" size="large" rounded="lg"
                class="wizard-footer__primary text-none ms-auto me-4 px-8" :loading="resubmittingApproval"
                @click="handleResubmitApproval">
                提交审批
              </v-btn>
            </div>
          </footer>
        </main>
      </div>
    </template>

    <div v-else class="wizard-submitted-wrap gm-card rounded-xl pa-6 flex-grow-1 min-h-0 d-flex flex-column">
      <div class="wizard-submitted__toolbar d-flex align-center justify-space-between mb-4">
        <v-btn variant="text" density="comfortable" class="px-1" @click="goBack">
          <v-icon start size="20">mdi-chevron-left</v-icon>
          返回
        </v-btn>
        <h1 class="text-h6 font-weight-semibold mb-0 text-high-emphasis">{{ pageTitle }}</h1>
        <span class="wizard-submitted__toolbar-pad" />
      </div>
      <div class="wizard-body wizard-body--submitted-step rounded-lg flex-grow-1 min-h-0">
        <StationAddStepSubmitted @done="handleSubmittedDone" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { eui } from 'ems-ui'
import { useStationAddBasic } from './useStationAddBasic'
import { validateStationUUID, add as addStation, update as updateStation, detail as getStationDetail, getStationApprovalList, submitStationApproval } from '@/api/device/station'
import { getTopology, postTopology } from '@/api/device/topo'
import { parseDeviceFlags } from '@/utils/deviceFlag'
import WizardStepper from './components/WizardStepper.vue'
import StationAddStepBasic from './components/StationAddStepBasic.vue'
import StationAddStepDevice from './components/StationAddStepDevice.vue'
import StationAddStepTopology from './components/StationAddStepTopology.vue'
import StationAddStepSubmitted from './components/StationAddStepSubmitted.vue'

const route = useRoute()
const router = useRouter()

const currentStep = ref(0)

/** 与路由 query.step 对齐（从电站列表「操作」进入对应 Tab）；策略/电价步骤暂未开放，落在拓扑步 */
const stepKeyToIndex = {
  basic: 0,
  device: 1,
  topo: 2,
  strategy: 2,
  price: 2,
}

function parseStepQuery(step, maxIdx) {
  if (step == null || step === '') return 0
  const s = String(step).toLowerCase().trim()
  if (Object.prototype.hasOwnProperty.call(stepKeyToIndex, s)) {
    return stepKeyToIndex[s]
  }
  const n = Number(s)
  if (Number.isInteger(n) && n >= 0) return Math.min(n, maxIdx)
  return 0
}

const pageTitle = '新增电站'

const steps = [
  {
    key: 'basic',
    title: '基本信息',
    desc: '项目与电站地址、投资方与业主、运营属性、列表优先级及地图与照片',
    icon: 'mdi-card-account-details-outline',
  },
  {
    key: 'device',
    title: '设备接入',
    desc: '将设备加入系统中，以便数据的传输与展示',
    icon: 'mdi-lan-connect',
  },
  {
    key: 'topo',
    title: '电站拓扑',
    desc: '绘制整个电站的拓扑结构，有利于直观的查看整站运行情况',
    icon: 'mdi-sitemap-outline',
  },
]

function getStepQueryByIndex(index) {
  return steps[Math.min(Math.max(index, 0), steps.length - 1)]?.key || 'basic'
}

async function syncWizardRoute(stepIndex = currentStep.value, stationId = effectiveStationId.value) {
  const step = getStepQueryByIndex(stepIndex)
  const nextStationId = stationId == null || stationId === '' ? '' : String(stationId)
  const currentStationId = route.query.stationId == null || route.query.stationId === '' ? '' : String(route.query.stationId)

  if (route.query.step === step && currentStationId === nextStationId) return

  const nextQuery = {
    ...route.query,
    step,
  }

  if (nextStationId) nextQuery.stationId = nextStationId
  else delete nextQuery.stationId

  await router.replace({
    path: route.path,
    query: nextQuery,
  })
}

const mobileProgressPct = computed(() => ((currentStep.value + 1) / steps.length) * 100)

const lastStepIndex = computed(() => steps.length - 1)
const isLastStep = computed(() => currentStep.value >= lastStepIndex.value)

watch(
  () => route.query.step,
  (s) => {
    currentStep.value = parseStepQuery(s, lastStepIndex.value)
  },
  { immediate: true },
)

watch(
  () => currentStep.value,
  (stepIndex) => {
    syncWizardRoute(stepIndex)
  },
)

const submittedView = ref(false)
const submitting = ref(false)
const topologySaving = ref(false)
const stationTopologyDraft = ref(null)
const topologyStepRef = ref(null)
const deviceStepRef = ref(null)
const serverConfigStep = ref(0)

const basicState = useStationAddBasic()

const {
  validate,
  buildBasicPayload,
  resetForm,
  form: basicForm,
  getPhotoUrl,
  setPhotoSlotFromStationPicture,
  normalizeAddressDetail,
} = basicState

const createdStationId = ref(null)
// 详情接口返回的 strategyConfig
const existedStrategyConfig = ref(null)
// 最新一条建站审批记录（rows[0]）；status: 0 待审批 / 1 通过 / 2 驳回
const latestApproval = ref(null)

/**
 * 「提交」按钮是否显示：
 * - 新增电站流程（URL 无 stationId）时展示
 * - 有 stationId 但尚未提交过审批（latestApproval 为空）时也展示，
 *   避免带 stationId 续填拓扑后没有任何提交入口
 */
const showSubmitBtn = computed(() => {
  const noStationId = route.query.stationId == null || route.query.stationId === ''
  const notSubmittedYet = latestApproval.value == null
  return noStationId || notSubmittedYet
})

/**
 * 「上一步」按钮是否升格为主按钮样式：
 */
const prevBtnIsPrimary = computed(() => isLastStep.value && !showSubmitBtn.value)

/**
 * 「提交审批」按钮：电站已驳回或审批通过时，允许再次发起审批
 */
const canResubmitApproval = computed(() => {
  return Number(latestApproval.value?.status) === 2
})

const resubmittingApproval = ref(false)

async function handleResubmitApproval() {
  const stationId = effectiveStationId.value
  if (stationId == null || stationId === '') {
    eui.showToast('未找到电站 ID，无法提交审批', 'error')
    return
  }
  await collectStepDrafts()
  resubmittingApproval.value = true
  try {
    if (stationTopologyDraft.value?.nodes?.length) {
      await postTopology({
        stationId,
        stationTopology: JSON.stringify(stationTopologyDraft.value),
      })
    }
    const targetConfigStep = stationTopologyDraft.value?.nodes?.length > 0 ? 3 : serverConfigStep.value
    await updateStationWithConfigStep(targetConfigStep)
    await submitStationApproval({ stationId: Number(stationId) })
    eui.showToast('已提交审批', 'success')
    await fetchLatestApproval(stationId)
  } catch (err) {
    console.error('提交审批失败:', err)
    eui.showToast(err?.msg || err?.message || '提交审批失败，请稍后重试', 'error')
  } finally {
    resubmittingApproval.value = false
  }
}

const effectiveStationId = computed(() => {
  const fromQuery = route.query.stationId
  if (fromQuery != null && fromQuery !== '') return fromQuery
  return createdStationId.value
})

/**
 * 查询该电站最新一条建站审批记录（rows[0]）。
 * 供「提交」按钮显隐判断使用：奇点客服角色对已通过 / 已驳回电站不显示提交按钮。
 */
async function fetchLatestApproval(stationId) {
  const sid = String(stationId ?? '').trim()
  if (!sid) {
    latestApproval.value = null
    return
  }
  try {
    const res = await getStationApprovalList({
      pageNum: 1,
      pageSize: 1,
      stationId: Number(sid),
    })
    const rows = Array.isArray(res?.rows) ? res.rows : []
    latestApproval.value = rows[0] || null
  } catch (err) {
    console.error('查询电站建站审批记录失败:', err)
    latestApproval.value = null
  }
}

watch(
  () => route.query.stationId,
  async (sid) => {
    basicState.mapSearchKeyword.value = ''
    if (sid == null || sid === '') {
      resetForm()
      createdStationId.value = null
      existedStrategyConfig.value = null
      serverConfigStep.value = 0
      latestApproval.value = null
      return
    }

    try {
      const res = await getStationDetail(sid)
      const station = res?.data?.station || {}
      basicForm.projectName = station.name || ''
      basicForm.projectCode = station.code || station.uuid || ''
      basicForm.projectShortName = station.stationAbbreviation || station.shortName || ''
      basicForm.maxpower = station.maxPower ?? station.maxpower ?? ''
      basicForm.commossioningTime = station.commossioningTime || ''
      basicForm.country = station.countryName || station.country || '中国'
      basicForm.area = station.area || ''
      basicForm.province = station.province || ''
      basicForm.city = station.city || ''
      basicForm.district = station.district || ''
      basicForm.township = station.township || ''
      basicForm.address = normalizeAddressDetail(station.address, {
        province: station.province,
        city: station.city,
        district: station.district,
        township: station.township,
      })
      basicForm.investorEpc = station.customer || ''
      basicForm.ownerEnterprise = station.operatingUnit || ''
      basicForm.channelFramework = station.devopsUnit || ''
      basicForm.industryCategory = station.industryCategory || ''
      basicForm.strategySwitch = !!station.strategySwitch
      basicForm.deviceFlags = parseDeviceFlags(station.deviceFlag)
      basicForm.businessMode = Number(station.businessModel) === 2 ? 'non_self_invest' : 'self_invest'
      basicForm.operationModel = Number(station.operatingModel) === 2 ? 'emc' : 'self'
      basicForm.electricityCategory = Number(station.classification) === 0 ? 'large_industrial' : 'industrial'
      basicForm.voltage = station.voltage
      basicForm.stationType = Number(station.energyStorageType) === 2 ? 'centralized' : 'distributed'
      basicForm.billingMode = Number(station.billingMode) === 1 ? 'capacity' : Number(station.billingMode) === 0 ? 'unknown' : 'demand'
      basicForm.capacity = station.capacity ?? ''
      basicForm.stationState = station.stationState ?? 1
      basicForm.meterModel = station.meterModel ?? ''
      basicForm.sort = station.sort ?? ''
      const longitude = Number(station.longitude)
      const latitude = Number(station.latitude)
      basicForm.longitude = Number.isFinite(longitude) ? longitude : null
      basicForm.latitude = Number.isFinite(latitude) ? latitude : null
      setPhotoSlotFromStationPicture(station.stationPicture || station.station_picture)
      const remoteStrategyConfig = res?.data?.strategyConfig
      existedStrategyConfig.value =
        remoteStrategyConfig && typeof remoteStrategyConfig === 'object' ? remoteStrategyConfig : null
      // 运营信息中的自定义控制权限 / 自动需量 / 联系手机号，均来自 strategyConfig
      basicForm.antiBackflow = Boolean(remoteStrategyConfig?.preventCounterCurrent)
      basicForm.transformerCapacityRatio = Boolean(remoteStrategyConfig?.transformerCapacityRatio)
      basicForm.reactivePowerWorking = Boolean(remoteStrategyConfig?.reactivePowerWorking)
      basicForm.autoDemand = remoteStrategyConfig ? Number(remoteStrategyConfig.autoDemand) !== -1 : false
      basicForm.contactPhone = remoteStrategyConfig?.phoneNumber || ''
      createdStationId.value = sid
      const remoteConfigStep = Number(station.configStep)
      serverConfigStep.value = Number.isFinite(remoteConfigStep) && remoteConfigStep > 0 ? remoteConfigStep : 0
      // 已有电站进入编辑：预取最新审批记录,用于「提交」按钮显隐判断
      fetchLatestApproval(sid)
    } catch (err) {
      console.error('获取电站基本信息失败:', err)
      resetForm()
      createdStationId.value = null
      existedStrategyConfig.value = null
      serverConfigStep.value = 0
      latestApproval.value = null
    }
  },
  { immediate: true },
)

function goToStationsList(query = {}) {
  router.push({
    path: '/it-operations/multi/stations',
    query,
  })
}

function goBack() {
  goToStationsList()
}

function prevStep() {
  if (currentStep.value > 0) currentStep.value -= 1
}

const advancing = ref(false)

function normalizeStationId(id) {
  const value = String(id ?? '').trim()
  return value || ''
}

function extractStationId(res) {
  return res?.data?.id ?? res?.data?.stationId ?? res?.data ?? ''
}

async function ensureStationCreated() {
  const existedStationId = normalizeStationId(effectiveStationId.value)

  const uuid = basicForm.projectCode?.trim()
  if (!uuid) {
    eui.showToast('请填写项目编码', 'error')
    return false
  }

  advancing.value = true
  try {
    const { station, strategyConfig } = buildBasicPayload()
    station.stationPicture = getPhotoUrl()
    // 基本信息步骤点击下一步,配置进度至少推进到 1(编辑已有电站时不倒退)
    const targetConfigStep = Math.max(Number(serverConfigStep.value) || 0, 1)
    station.configStep = targetConfigStep

    if (existedStationId) {
      station.stationId = Number.isNaN(Number(existedStationId)) ? existedStationId : Number(existedStationId)
      // 编辑场景：保留后端已有的其它 strategyConfig 字段，
      // 但表单显式维护的五项必须以表单值为准，否则用户的修改会被远端旧值覆盖
      const payloadStrategyConfig = existedStrategyConfig.value
        ? { ...existedStrategyConfig.value, ...strategyConfig }
        : strategyConfig
      await updateStation({ station, strategyConfig: payloadStrategyConfig })
      createdStationId.value = existedStationId
      serverConfigStep.value = targetConfigStep
      return true
    }

    const uuidRes = await validateStationUUID(uuid)
    if (uuidRes && uuidRes.data === false) {
      eui.showToast(uuidRes.msg, 'error')
      return false
    }
    const res = await addStation({ station, strategyConfig })
    const sid = extractStationId(res)
    if (sid != null && sid !== '') {
      createdStationId.value = sid
      serverConfigStep.value = targetConfigStep
      return true
    }
    return false
  } catch (err) {
    console.error('保存电站基本信息失败:', err)
    return false
  } finally {
    advancing.value = false
  }
}

async function advanceFromBasicStep() {
  return ensureStationCreated()
}

// 全量更新电站信息,并把配置进度尝试推进到 targetStep
// 通过 Math.max(serverConfigStep, targetStep) 保证不会把已有进度改小
async function updateStationWithConfigStep(targetStep) {
  const stationId = normalizeStationId(effectiveStationId.value)
  const { station, strategyConfig } = buildBasicPayload()
  station.stationPicture = getPhotoUrl()
  station.stationId = Number.isNaN(Number(stationId)) ? stationId : Number(stationId)
  const finalStep = Math.max(Number(serverConfigStep.value) || 0, Number(targetStep) || 0)
  station.configStep = finalStep
  const payloadStrategyConfig = existedStrategyConfig.value
    ? { ...existedStrategyConfig.value, ...strategyConfig }
    : strategyConfig
  await updateStation({ station, strategyConfig: payloadStrategyConfig })
  serverConfigStep.value = finalStep
}

// 设备接入步骤进入下一步：校验储能系统必须有内容,通过后把配置进度推进到 2
async function advanceFromDeviceStep() {
  const stationId = normalizeStationId(effectiveStationId.value)
  if (!stationId) {
    eui.showToast('请先完成基本信息创建', 'error')
    return false
  }
  if (!deviceStepRef.value?.hasEnergyStorage?.()) {
    eui.showToast('请先添加储能系统后再继续', 'error')
    return false
  }
  advancing.value = true
  try {
    await updateStationWithConfigStep(2)
    return true
  } catch (err) {
    console.error('更新电站配置进度失败:', err)
    return false
  } finally {
    advancing.value = false
  }
}

async function nextStep() {
  if (currentStep.value === 0) {
    if (!validate()) return
    const ok = await advanceFromBasicStep()
    if (!ok) return
  } else if (currentStep.value === 1) {
    const ok = await advanceFromDeviceStep()
    if (!ok) return
  }
  if (currentStep.value < steps.length - 1) {
    const nextStepIndex = currentStep.value + 1
    const nextStationId = effectiveStationId.value
    currentStep.value = nextStepIndex
    await syncWizardRoute(nextStepIndex, nextStationId)
  }
}

async function handleStepChange(nextStepIndex) {
  if (nextStepIndex === currentStep.value) return
  if (nextStepIndex < currentStep.value) {
    currentStep.value = nextStepIndex
    await syncWizardRoute(nextStepIndex)
    return
  }
  if (currentStep.value === 0) {
    if (!validate()) return
    const ok = await ensureStationCreated()
    if (!ok) return
  } else if (currentStep.value === 1) {
    const ok = await advanceFromDeviceStep()
    if (!ok) return
  }
  const targetStepIndex = Math.min(nextStepIndex, lastStepIndex.value)
  currentStep.value = targetStepIndex
  await syncWizardRoute(targetStepIndex)
}


async function fetchTopologyData() {
  let json = {}
  try {
    const res = await getTopology(effectiveStationId.value)
    const topoData = res?.data?.stationTopology
    json = JSON.parse(topoData ?? '{}')
  } catch (error) {
    json = {}
  }

  console.log('topo json', json);
  if (json.version === 2) {
    topologyStepRef.value?.loadTopologyJson(json)
  }
}


async function collectStepDrafts() {
  const liveTopo = topologyStepRef.value?.getTopologyJson?.()
  if (liveTopo?.nodes?.length) {
    stationTopologyDraft.value = liveTopo
  }
}

async function handleTopologySave(data) {
  const stationId = effectiveStationId.value
  if (stationId == null || stationId === '') {
    eui.showToast('请先完成基本信息创建', 'error')
    return
  }

  stationTopologyDraft.value = data
  topologySaving.value = true
  try {
    await postTopology({
      stationId,
      stationTopology: JSON.stringify(data),
    })
    // 拓扑不为空才把配置进度推进到 3；为空则维持后端原值(不覆盖已有进度)
    const targetConfigStep = data?.nodes?.length > 0 ? 3 : serverConfigStep.value
    await updateStationWithConfigStep(targetConfigStep)
    eui.showToast('拓扑已保存', 'success')
  } catch (err) {
    console.error('保存电站拓扑失败:', err)
    eui.showToast('拓扑保存失败，请重试', 'error')
  } finally {
    topologySaving.value = false
  }
}

async function handleSubmit() {
  if (currentStep.value !== lastStepIndex.value) {
    eui.showToast('请先完成电站拓扑后再提交', 'info')
    return
  }
  const stationId = effectiveStationId.value
  if (stationId == null || stationId === '') {
    eui.showToast('请先完成基本信息创建', 'error')
    return
  }
  await collectStepDrafts()
  submitting.value = true
  try {
    if (stationTopologyDraft.value?.nodes?.length) {
      await postTopology({
        stationId,
        stationTopology: JSON.stringify(stationTopologyDraft.value),
      })
    }

    // 查询该电站的建站审核记录：空数组 → 进入「已提交，正在审核」页；否则直接返回列表
    const approvalRes = await getStationApprovalList({
      pageNum: 1,
      pageSize: 10,
      stationId: Number(stationId),
    })
    const approvalRows = Array.isArray(approvalRes?.rows) ? approvalRes.rows : []

    if (approvalRows.length === 0) {
      // 提交审核前,先全量更新电站信息；拓扑不为空才把配置进度推进到 3,否则维持后端原值
      const targetConfigStep = stationTopologyDraft.value?.nodes?.length > 0 ? 3 : serverConfigStep.value
      await updateStationWithConfigStep(targetConfigStep)
      await submitStationApproval({ stationId: Number(stationId) })
      submittedView.value = true
    } else {
      goToStationsList()
    }
  } catch (err) {
    console.error('操作失败:', err)
  } finally {
    submitting.value = false
  }
}

function handleSubmittedDone() {
  submittedView.value = false
  goToStationsList({ status: 'pending' })
}

watch(currentStep, (newVal) => {
  if (newVal == 2) {
    fetchTopologyData()
    // 每次进入拓扑步再刷一次最新审批状态,确保「提交」按钮显隐与当前审批状态一致
    fetchLatestApproval(effectiveStationId.value)
  }
}, { immediate: true })
</script>

<style scoped lang="scss">
.station-add-page {
  box-sizing: border-box;
  overflow: hidden;
}

.wizard-frame {
  display: grid;
  grid-template-columns: minmax(240px, 280px) minmax(0, 1fr);
  align-items: stretch;
  width: 100%;
  min-height: 0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(8, 40, 87, 0.05);
}

.wizard-sidebar {
  display: flex;
  flex-direction: column;
  padding: 24px 20px 20px;
  background: linear-gradient(187deg, rgba(188, 201, 250, 0.22) 0%, rgba(255, 255, 255, 0.55) 42%, #fff 100%);
  border-right: 1px solid var(--color-td-border);
  min-height: 0;
}

.wizard-sidebar__toolbar {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.wizard-sidebar__back {
  color: var(--color-text) !important;
  opacity: 0.85;
}

.wizard-sidebar__stepper {
  flex: 1;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  padding-right: 4px;
}

.wizard-sidebar__link {
  margin-top: auto;
  padding: 12px 0 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  font-size: 0.875rem;
  color: var(--color-text);
  opacity: 0.72;
  line-height: 1.45;
  transition: opacity 0.2s ease;
}

.wizard-sidebar__link:hover {
  opacity: 1;
}

.wizard-sidebar__link:hover .wizard-sidebar__link-strong {
  text-decoration: underline;
}

.wizard-sidebar__link-strong {
  color: var(--color-primary);
  font-weight: 700;
}

.wizard-mobile-head {
  display: none;
  padding: 16px 16px 12px;
  border-bottom: 1px solid var(--color-td-border);
  background: #fff;
}

.wizard-mobile-progress {
  height: 6px;
  border-radius: 999px;
  background: var(--color-th-border);
  overflow: hidden;
}

.wizard-mobile-progress__fill {
  height: 100%;
  border-radius: inherit;
  background: var(--color-primary);
  transition: width 0.25s ease;
}

.wizard-main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: #fff;

}

.wizard-body {
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: rgba(255, 255, 255, 0.96);
  padding: 20px clamp(16px, 2.5vw, 28px) 20px;
}

.wizard-body--device-step {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.wizard-body--topo-step {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.wizard-body--submitted-step {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  min-height: 280px;
}

.wizard-footer {
  flex-shrink: 0;
  margin-top: 16px;
  padding-top: 16px;
  padding-bottom: 16px;
  border-top: 1px solid var(--color-td-border);
  // max-width: min(920px, 100%);
  margin-inline: auto;
  width: 100%;
}

.wizard-footer__primary {
  flex: 0 0 auto;
  min-height: 40px !important;
  padding-inline: 18px !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  letter-spacing: 0.01em;
  box-shadow: 0 4px 12px rgba(43, 87, 238, 0.22);
}

.wizard-footer__primary:disabled {
  opacity: 0.45;
  box-shadow: none;
}

.wizard-footer__row {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px 10px;
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  padding-left: 16px;
}

.wizard-submitted-wrap {
  max-width: 960px;
  margin-inline: auto;
  width: 100%;
  box-shadow: 0 2px 8px rgba(8, 40, 87, 0.05);
}

.wizard-submitted__toolbar-pad {
  width: 72px;
}

@media (max-width: 1279px) {
  .wizard-frame {
    grid-template-columns: 1fr;
  }

  .wizard-sidebar {
    display: none !important;
  }

  .wizard-mobile-head {
    display: flex !important;
  }

  .wizard-main {
    padding-top: 12px;
  }
}

@media (max-width: 599px) {
  .wizard-footer__row {
    gap: 6px 8px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .wizard-mobile-progress__fill {
    transition: none;
  }
}
</style>
